import { useState } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';

import { store } from 'redux/store';
import { setLogged } from 'redux/actionCreators/userActions';

import { DataLayerEmptyScript } from 'scripts/DataLayer';
import { GaConnectorInputs, GaConnectorScript } from 'scripts/GaConnector';
import { GoogleTagManagerScript } from 'scripts/GoogleTagManager';
import { MetaPixelScript } from 'scripts/MetaPixel';

import { blacklistForMembershipBanner } from 'sections/Home/data';
import { Environment } from 'sections/Home/types';
import { webSchemas } from 'lib/schemas';
import { apiMap } from 'api/apiMap';
import { host } from 'utils/constants';
import { DefaultMetaData } from 'sections/Home/data';

export const useApp = (Component: any, pageProps?: any) => {
  const {
    title: propsTitle,
    description: propsDescription,
    keywords,
    path: pathProps = '/',
    pixelMeta = false,
    authorName,
    image,
    publishDate,
    ...otherProps
  } = pageProps;

  const getLayout = Component.getLayout || ((page: any) => page);

  const [{ }, setCookie, removeCookie] = useCookies(['user']);
  const [isUserLogged, setIsUserLogged] = useState<boolean>(false);

  const getSchema = () => {
    switch (true) {
      case pathProps.startsWith('/blog'):
        return webSchemas.post({
          title: propsTitle,
          description: propsDescription,
          path: pathProps,
          author: authorName,
          image: image,
          publishDate: publishDate,
        });
      case !!otherProps.post:
        return webSchemas.post({
          title: propsTitle,
          description: propsDescription,
          path: pathProps,
          author: authorName,
          image: image,
          publishDate: publishDate,
          post: otherProps.post,
        });
      case pathProps.startsWith('/freight-dictionary/'):
        return webSchemas.freightDictionaryChildren({
          title: propsTitle,
          path: pathProps,
        });
      case pathProps.startsWith('/freight-forwarder/'):
        return webSchemas.freightForwarderChildren({
          title: propsTitle,
          path: pathProps,
        });
      default: // @ts-ignore
        return webSchemas?.[pathProps]?.(propsTitle ? propsTitle : DefaultMetaData.title, propsDescription ? propsDescription : DefaultMetaData.description, pathProps)
          || webSchemas['/'](propsTitle ? propsTitle : DefaultMetaData.title, propsDescription ? propsDescription : DefaultMetaData.description, pathProps)
    }
  };

  const addScripts = (environment: Environment, pageProps?: any) => {
    return (
      <>
        <GaConnectorInputs />

        {!!pageProps.pixelMeta
          ?
          <MetaPixelScript />
          :
          null
        }

        {environment === 'development'
          ?
          <DataLayerEmptyScript />
          :
          null
        }

        {environment === 'production'
          ?
          <>
            <GaConnectorScript />
            <GoogleTagManagerScript />
          </>
          :
          null
        }
      </>

    );
  };

  // const addMeta = (environment: Environment) => {
  //   return (
  //     <>
  //       {!!propsTitle
  //         ?
  //         <>
  //           <title>{propsTitle}</title>
  //           <meta property="og:title" content={propsTitle} />
  //           <meta name="twitter:title" content={propsTitle} />
  //         </>
  //         :
  //         <>
  //           <title>{DefaultMetaData.title}</title>
  //           <meta property="og:title" content={DefaultMetaData.title} />
  //           <meta name="twitter:title" content={DefaultMetaData.title} />
  //         </>
  //       }

  //       {!!propsDescription
  //         ?
  //         <>
  //           <meta property="og:description" content={propsDescription} />
  //           <meta name="twitter:description" content={propsDescription} />
  //           <meta name="twitter:image" content={propsDescription} />
  //           <meta name="description" content={propsDescription} />
  //         </>
  //         :
  //         <>
  //           <meta property="og:description" content={DefaultMetaData.description} />
  //           <meta name="twitter:description" content={DefaultMetaData.description} />
  //           <meta name="twitter:image" content={DefaultMetaData.description} />
  //           <meta name="description" content={DefaultMetaData.description} />
  //         </>
  //       }

  //       {!!keywords
  //         ?
  //         <meta name="keywords" content={keywords} />
  //         :
  //         <meta
  //           name="keywords"
  //           content="digital freight alliance, freight alliance, freight online, online freight marketplace, ocean freight online, logistics member portal"
  //         />
  //       }

  //       <meta property="og:site_name" content="DF Alliance" />
  //       <meta property="og:type" content="website" />
  //       <meta property="og:image" content="https://www.df-alliance.com/images/global/logo.svg" />
  //       <meta name="twitter:card" content="summary" />
  //       <meta name="twitter:site" content="DF Alliance" />
  //       <meta name="twitter:image" content="https://www.df-alliance.com/images/global/logo.svg" />

  //       <link rel="icon" href="/images/favicon.ico" />

  //       {environment === 'production'
  //         ?
  //         <>
  //           <meta property="og:url" content={`https://www.df-alliance.com${pathProps}`} />
  //           <link rel="canonical" href={`https://www.df-alliance.com${pathProps}`} />
  //           <meta name="google-site-verification" content="mZjCSKb2qrksNA7tbDfY-8aP7DIM2f4H47ukvHAGIZc" />
  //         </>
  //         :
  //         null
  //       }

  //       <script
  //         type="application/ld+json"
  //         dangerouslySetInnerHTML={{ __html: getSchema() }}
  //       />
  //     </>
  //   );
  // };

  const fetchData = async () => {
    const {
      data: { data: user },
    } = await axios(apiMap.initialAuthenticate);

    if (user) {
      setCookie(
        'user',
        {
          company: user.company,
          logo: user.logo,
          validity: user.validity,
          first_name: user.first_name,
          type: user.type,
          name: user.name,
          email: user.email,
          phone: user.phone,
          isAdmin: user.isAdmin,
          hash_id: user.hash_id,
          hash: user.hash,
        },
        { path: '/' }
      );
    } else {
      removeCookie('user', { path: '/' });
    }

    store.dispatch(setLogged(!!user?.hash));

    setIsUserLogged(!!user?.hash);

    const app = document.body.querySelector<HTMLDivElement>('#app');
    if (host !== 'loc' && app) app.removeAttribute('data-page');
  };

  const checkPageForBlacklist = (pageProps: any) => {
    return blacklistForMembershipBanner.some((item: string) => item === pageProps?.path);
  };

  return { 
    checkPageForBlacklist, 
    addScripts, 
    // addMeta, 
    getLayout, 
    otherProps, 
    fetchData, 
    isUserLogged, 
    getSchema, 
  };
};